import React, { useState } from 'react';
import { SurveyDefinition } from '../../types/survey.types';
import { PDFExportOptions } from '../../utils/pdfExport';

interface PDFExportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onExport: (options: PDFExportOptions) => Promise<void>;
  survey: SurveyDefinition;
  totalResponses: number;
}

const PDFExportModal: React.FC<PDFExportModalProps> = ({
  isOpen,
  onClose,
  onExport,
  survey,
  totalResponses,
}) => {
  const [exportTemplate, setExportTemplate] = useState<'executive' | 'full' | 'custom'>('full');
  const [isExporting, setIsExporting] = useState(false);

  // Section inclusions
  const [includeSummary, setIncludeSummary] = useState(true);
  const [includeOverview, setIncludeOverview] = useState(true);
  const [includeCharts, setIncludeCharts] = useState(true);
  const [includeDetailedBreakdown, setIncludeDetailedBreakdown] = useState(true);
  const [includeRawData, setIncludeRawData] = useState(false);
  const [includeInsights, setIncludeInsights] = useState(true);

  // Detail options
  const [showDualDenominators, setShowDualDenominators] = useState(true);
  const [showConditionalContext, setShowConditionalContext] = useState(true);
  const [showPercentages, setShowPercentages] = useState(true);
  const [showCounts, setShowCounts] = useState(true);

  // Branding
  const [includeLogo, setIncludeLogo] = useState(true);
  const [includeFooter, setIncludeFooter] = useState(true);
  const [reportTitle, setReportTitle] = useState('Analytics Report');
  const [reportSubtitle, setReportSubtitle] = useState('Comprehensive Survey Analysis');
  const [generatedBy, setGeneratedBy] = useState('');

  // Layout options
  const [lineSpacing, setLineSpacing] = useState<'compact' | 'normal' | 'spacious'>('compact');
  const [pageMargins, setPageMargins] = useState<'narrow' | 'normal' | 'wide'>('narrow');

  // Section/question filters
  const [selectedSections, setSelectedSections] = useState<string[]>(
    survey.sections.map(s => s.id)
  );

  if (!isOpen) return null;

  const handleTemplateChange = (template: 'executive' | 'full' | 'custom') => {
    setExportTemplate(template);

    if (template === 'executive') {
      // Executive summary: Overview only, minimal details
      setIncludeSummary(true);
      setIncludeOverview(true);
      setIncludeCharts(false);
      setIncludeDetailedBreakdown(false);
      setIncludeRawData(false);
      setIncludeInsights(true); // Include insights in executive summary
      setShowDualDenominators(false);
      setShowConditionalContext(false);
    } else if (template === 'full') {
      // Full report: Everything
      setIncludeSummary(true);
      setIncludeOverview(true);
      setIncludeCharts(true);
      setIncludeDetailedBreakdown(true);
      setIncludeRawData(false);
      setIncludeInsights(true);
      setShowDualDenominators(true);
      setShowConditionalContext(true);
      setShowPercentages(true);
      setShowCounts(true);
    }
    // Custom: Let user configure
  };

  const handleExport = async () => {
    setIsExporting(true);

    const options: PDFExportOptions = {
      surveyTitle: survey.title,
      surveyDescription: survey.description,
      includeSummary,
      includeOverview,
      includeCharts,
      includeDetailedBreakdown,
      includeRawData,
      includeInsights,
      showDualDenominators,
      showConditionalContext,
      showPercentages,
      showCounts,
      selectedSections,
      includeLogo,
      includeFooter,
      reportTitle,
      reportSubtitle,
      generatedBy: generatedBy || undefined,
      generatedDate: new Date(),
      lineSpacing,
      pageMargins,
    };

    try {
      await onExport(options);
      onClose();
    } catch (error) {
      console.error('[PDFExportModal] Export failed:', error);
    } finally {
      setIsExporting(false);
    }
  };

  const toggleSection = (sectionId: string) => {
    if (selectedSections.includes(sectionId)) {
      setSelectedSections(selectedSections.filter(id => id !== sectionId));
    } else {
      setSelectedSections([...selectedSections, sectionId]);
    }
  };

  const selectAllSections = () => {
    setSelectedSections(survey.sections.map(s => s.id));
  };

  const deselectAllSections = () => {
    setSelectedSections([]);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div
        className="bg-white rounded-lg shadow-xl max-w-4xl w-full overflow-hidden flex flex-col"
        style={{ fontFamily: 'var(--font-body)', maxHeight: 'calc(100vh - 2rem)' }}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b" style={{ borderColor: 'var(--color-border)' }}>
          <div className="flex items-center justify-between">
            <div>
              <h2 style={{
                fontFamily: 'var(--font-heading)',
                fontSize: '1.5rem',
                fontWeight: 600,
                color: 'var(--color-primary)'
              }}>
                Export Analytics Report
              </h2>
              <p style={{ fontSize: '0.875rem', color: 'var(--color-muted)', marginTop: '0.25rem' }}>
                Customize your professional PDF report
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-2xl"
              style={{ color: 'var(--color-muted)' }}
            >
              ×
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="px-6 py-4 overflow-y-auto flex-1">
          {/* Template Selection */}
          <div className="mb-6">
            <h3 style={{
              fontSize: '1rem',
              fontWeight: 600,
              color: 'var(--color-primary)',
              marginBottom: '0.75rem'
            }}>
              Report Template
            </h3>
            <div className="grid grid-cols-3 gap-3">
              {[
                { id: 'executive', name: 'Executive Summary', desc: 'High-level overview only' },
                { id: 'full', name: 'Full Report', desc: 'Complete detailed analysis' },
                { id: 'custom', name: 'Custom', desc: 'Choose what to include' },
              ].map(template => (
                <button
                  key={template.id}
                  onClick={() => handleTemplateChange(template.id as any)}
                  className="p-4 rounded-lg border-2 text-left transition-all"
                  style={{
                    borderColor: exportTemplate === template.id ? 'var(--color-accent)' : 'var(--color-border)',
                    backgroundColor: exportTemplate === template.id ? 'rgba(199, 161, 74, 0.05)' : 'transparent'
                  }}
                >
                  <div style={{
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    color: exportTemplate === template.id ? 'var(--color-accent)' : 'var(--color-primary)'
                  }}>
                    {template.name}
                  </div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--color-muted)', marginTop: '0.25rem' }}>
                    {template.desc}
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Report Details */}
          <div className="mb-6">
            <h3 style={{
              fontSize: '1rem',
              fontWeight: 600,
              color: 'var(--color-primary)',
              marginBottom: '0.75rem'
            }}>
              Report Details
            </h3>
            <div className="space-y-3">
              <div>
                <label style={{ fontSize: '0.875rem', color: 'var(--color-muted)', display: 'block', marginBottom: '0.25rem' }}>
                  Report Title
                </label>
                <input
                  type="text"
                  value={reportTitle}
                  onChange={(e) => setReportTitle(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg"
                  style={{ borderColor: 'var(--color-border)', fontFamily: 'var(--font-body)' }}
                />
              </div>
              <div>
                <label style={{ fontSize: '0.875rem', color: 'var(--color-muted)', display: 'block', marginBottom: '0.25rem' }}>
                  Subtitle
                </label>
                <input
                  type="text"
                  value={reportSubtitle}
                  onChange={(e) => setReportSubtitle(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg"
                  style={{ borderColor: 'var(--color-border)', fontFamily: 'var(--font-body)' }}
                />
              </div>
              <div>
                <label style={{ fontSize: '0.875rem', color: 'var(--color-muted)', display: 'block', marginBottom: '0.25rem' }}>
                  Generated By (optional)
                </label>
                <input
                  type="text"
                  value={generatedBy}
                  onChange={(e) => setGeneratedBy(e.target.value)}
                  placeholder="Your name or organization"
                  className="w-full px-3 py-2 border rounded-lg"
                  style={{ borderColor: 'var(--color-border)', fontFamily: 'var(--font-body)' }}
                />
              </div>
            </div>
          </div>

          {/* Layout Options */}
          <div className="mb-6">
            <h3 style={{
              fontSize: '1rem',
              fontWeight: 600,
              color: 'var(--color-primary)',
              marginBottom: '0.75rem'
            }}>
              Layout Options
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label style={{ fontSize: '0.875rem', color: 'var(--color-muted)', display: 'block', marginBottom: '0.5rem' }}>
                  Line Spacing
                </label>
                <select
                  value={lineSpacing}
                  onChange={(e) => setLineSpacing(e.target.value as any)}
                  className="w-full px-3 py-2 border rounded-lg"
                  style={{ borderColor: 'var(--color-border)', fontFamily: 'var(--font-body)' }}
                >
                  <option value="compact">Compact (More content per page)</option>
                  <option value="normal">Normal</option>
                  <option value="spacious">Spacious (More readable)</option>
                </select>
              </div>
              <div>
                <label style={{ fontSize: '0.875rem', color: 'var(--color-muted)', display: 'block', marginBottom: '0.5rem' }}>
                  Page Margins
                </label>
                <select
                  value={pageMargins}
                  onChange={(e) => setPageMargins(e.target.value as any)}
                  className="w-full px-3 py-2 border rounded-lg"
                  style={{ borderColor: 'var(--color-border)', fontFamily: 'var(--font-body)' }}
                >
                  <option value="narrow">Narrow (More real estate)</option>
                  <option value="normal">Normal</option>
                  <option value="wide">Wide (More whitespace)</option>
                </select>
              </div>
            </div>
          </div>

          {exportTemplate === 'custom' && (
            <>
              {/* Content Sections */}
              <div className="mb-6">
                <h3 style={{
                  fontSize: '1rem',
                  fontWeight: 600,
                  color: 'var(--color-primary)',
                  marginBottom: '0.75rem'
                }}>
                  Include in Report
                </h3>
                <div className="space-y-2">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={includeSummary}
                      onChange={(e) => setIncludeSummary(e.target.checked)}
                      className="mr-3"
                    />
                    <span style={{ fontSize: '0.875rem', color: 'var(--color-primary)' }}>
                      Executive Summary
                    </span>
                  </label>
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={includeOverview}
                      onChange={(e) => setIncludeOverview(e.target.checked)}
                      className="mr-3"
                    />
                    <span style={{ fontSize: '0.875rem', color: 'var(--color-primary)' }}>
                      Survey Overview
                    </span>
                  </label>
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={includeCharts}
                      onChange={(e) => setIncludeCharts(e.target.checked)}
                      className="mr-3"
                    />
                    <span style={{ fontSize: '0.875rem', color: 'var(--color-primary)' }}>
                      Charts & Visualizations
                    </span>
                  </label>
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={includeDetailedBreakdown}
                      onChange={(e) => setIncludeDetailedBreakdown(e.target.checked)}
                      className="mr-3"
                    />
                    <span style={{ fontSize: '0.875rem', color: 'var(--color-primary)' }}>
                      Detailed Question Breakdown
                    </span>
                  </label>
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={includeInsights}
                      onChange={(e) => setIncludeInsights(e.target.checked)}
                      className="mr-3"
                    />
                    <span style={{ fontSize: '0.875rem', color: 'var(--color-primary)' }}>
                      AI-Generated Insights
                      <span style={{ fontSize: '0.75rem', color: 'var(--color-accent)', marginLeft: '0.5rem', fontWeight: 500 }}>
                        ✨ NEW
                      </span>
                    </span>
                  </label>
                </div>
              </div>

              {/* Detail Level */}
              <div className="mb-6">
                <h3 style={{
                  fontSize: '1rem',
                  fontWeight: 600,
                  color: 'var(--color-primary)',
                  marginBottom: '0.75rem'
                }}>
                  Detail Level
                </h3>
                <div className="space-y-2">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={showDualDenominators}
                      onChange={(e) => setShowDualDenominators(e.target.checked)}
                      className="mr-3"
                    />
                    <span style={{ fontSize: '0.875rem', color: 'var(--color-primary)' }}>
                      Show Dual Denominators
                      <span style={{ fontSize: '0.75rem', color: 'var(--color-muted)', marginLeft: '0.5rem' }}>
                        (% of question respondents AND % of total)
                      </span>
                    </span>
                  </label>
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={showConditionalContext}
                      onChange={(e) => setShowConditionalContext(e.target.checked)}
                      className="mr-3"
                    />
                    <span style={{ fontSize: '0.875rem', color: 'var(--color-primary)' }}>
                      Show Conditional Question Context
                    </span>
                  </label>
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={showPercentages}
                      onChange={(e) => setShowPercentages(e.target.checked)}
                      className="mr-3"
                    />
                    <span style={{ fontSize: '0.875rem', color: 'var(--color-primary)' }}>
                      Show Percentages
                    </span>
                  </label>
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={showCounts}
                      onChange={(e) => setShowCounts(e.target.checked)}
                      className="mr-3"
                    />
                    <span style={{ fontSize: '0.875rem', color: 'var(--color-primary)' }}>
                      Show Response Counts
                    </span>
                  </label>
                </div>
              </div>

              {/* Section Selection */}
              <div className="mb-6">
                <div className="flex items-center justify-between mb-3">
                  <h3 style={{
                    fontSize: '1rem',
                    fontWeight: 600,
                    color: 'var(--color-primary)'
                  }}>
                    Include Sections
                  </h3>
                  <div className="space-x-2">
                    <button
                      onClick={selectAllSections}
                      className="text-xs"
                      style={{ color: 'var(--color-accent)', fontWeight: 500 }}
                    >
                      Select All
                    </button>
                    <span style={{ color: 'var(--color-muted)' }}>|</span>
                    <button
                      onClick={deselectAllSections}
                      className="text-xs"
                      style={{ color: 'var(--color-muted)', fontWeight: 500 }}
                    >
                      Deselect All
                    </button>
                  </div>
                </div>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {survey.sections.map(section => (
                    <label key={section.id} className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedSections.includes(section.id)}
                        onChange={() => toggleSection(section.id)}
                        className="mr-3"
                      />
                      <span style={{ fontSize: '0.875rem', color: 'var(--color-primary)' }}>
                        {section.title}
                        <span style={{ fontSize: '0.75rem', color: 'var(--color-muted)', marginLeft: '0.5rem' }}>
                          ({section.questions.length} questions)
                        </span>
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            </>
          )}

          {/* Branding Options */}
          <div className="mb-6">
            <h3 style={{
              fontSize: '1rem',
              fontWeight: 600,
              color: 'var(--color-primary)',
              marginBottom: '0.75rem'
            }}>
              Branding
            </h3>
            <div className="space-y-2">
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={includeLogo}
                  onChange={(e) => setIncludeLogo(e.target.checked)}
                  className="mr-3"
                />
                <span style={{ fontSize: '0.875rem', color: 'var(--color-primary)' }}>
                  Include Crisci Create Logo & Branding
                </span>
              </label>
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={includeFooter}
                  onChange={(e) => setIncludeFooter(e.target.checked)}
                  className="mr-3"
                />
                <span style={{ fontSize: '0.875rem', color: 'var(--color-primary)' }}>
                  Include Page Footers
                </span>
              </label>
            </div>
          </div>

          {/* Summary */}
          <div className="p-4 rounded-lg" style={{ backgroundColor: 'var(--color-surface)', border: '1px solid var(--color-border)' }}>
            <h4 style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--color-primary)', marginBottom: '0.5rem' }}>
              Report Summary
            </h4>
            <div style={{ fontSize: '0.75rem', color: 'var(--color-muted)' }}>
              <p>Survey: {survey.title}</p>
              <p>Total Responses: {totalResponses}</p>
              <p>Sections: {selectedSections.length} of {survey.sections.length}</p>
              <p>Template: {exportTemplate === 'executive' ? 'Executive Summary' : exportTemplate === 'full' ? 'Full Report' : 'Custom'}</p>
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="px-6 py-4 border-t flex justify-end space-x-3" style={{ borderColor: 'var(--color-border)' }}>
          <button
            onClick={onClose}
            disabled={isExporting}
            className="btn btn-secondary"
          >
            Cancel
          </button>
          <button
            onClick={handleExport}
            disabled={isExporting || selectedSections.length === 0}
            className="btn btn-primary"
            style={{ minWidth: '120px' }}
          >
            {isExporting ? (
              <span className="flex items-center gap-2">
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                Generating...
              </span>
            ) : (
              '📄 Generate PDF'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PDFExportModal;
